import { useEffect } from "react";
import * as P from "./style";
import { FaArrowLeft } from "react-icons/fa";
import InputRange from "react-input-range";
import "react-input-range/lib/css/index.css";
import { CommonButton } from "../../common-styles";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import { useNavigate } from "react-router-dom";
import CommonLoader from "../../Components/CommonLoader";
import {
  fetchMemberShipDetails,
  updateMemberShipDetails,
  updatePricing,
} from "../../Redux/slices/settingSlice";
import { setMembershipPrices } from "../../Redux/slices/userSlice";
import {
  ALLOWED_PLAN_SET_VALUES,
  snapToAllowedSetPrice,
  mapSetPriceToActualPrice,
  getSetPriceFromActualPrice,
} from "../../Utils/pricing";
import {
  ALLOWED_MESSAGE_SET_VALUES,
  getMessageSetPriceFromActualPrice,
  mapMessageSetPriceToActualPrice,
  snapToAllowedMessageSetPrice,
} from "../../Utils/messagePricing";

const MembershipPricing = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { loading: userLoading, id: userId } = useSelector(
    (state: RootState) => state.user
  );
  const settings = useSelector((state: RootState) => state.setting);
  const pricingSettings = settings.memberDetails;

  const applyChanges = async () => {
    const payload = {
      monthlyPrice: pricingSettings.monthlyPrice.toString(),
      messagePrice: pricingSettings.messagePrice.toString(),
    };

    const result = await dispatch(updateMemberShipDetails(payload));

    if (updateMemberShipDetails.fulfilled.match(result)) {
      dispatch(setMembershipPrices(payload));
    }
  };

  const handlePriceChange = (
    pricingKey: "monthlyPrice" | "messagePrice",
    value: any
  ) => {
    if (typeof value !== "number") return;
    dispatch(
      updatePricing({
        paramKey: "memberDetails",
        pricingKey,
        priceValue: value.toString(),
      })
    );
  };

  useEffect(() => {
    if (userId) dispatch(fetchMemberShipDetails(userId));
  }, [dispatch, userId]);

  if (!userLoading && !userId) return <CommonLoader message="loading..." />;

  const currentMonthlyPrice = parseInt(settings.memberDetails.monthlyPrice);
  const currentSliderValue = getSetPriceFromActualPrice(currentMonthlyPrice);

  const currentMessagePriceService = parseInt(
    settings.memberDetails.messagePrice
  );
  const currentMessagePriceDisplay = getMessageSetPriceFromActualPrice(
    currentMessagePriceService
  );

  return (
    <P.Pricing className="text-white">
      <h1 className="text-white font-noto flex items-center gap-5 md:gap-10">
        <span onClick={() => navigate("/account")}>
          <FaArrowLeft
            className="cursor-pointer"
            onClick={() => navigate("/account")}
            size={30}
          />
        </span>
        <span className="font-semibold">Membership</span>
      </h1>

      <div>
        <p className="text-white uppercase md:text-lg">
          Set monthly membership price
        </p>
        <div className="mt-12">
          <InputRange
            step={1}
            allowSameValues
            minValue={Math.min(...ALLOWED_PLAN_SET_VALUES)}
            maxValue={Math.max(...ALLOWED_PLAN_SET_VALUES)}
            value={currentSliderValue}
            onChange={(value) => {
              if (typeof value !== "number") return;
              const snappedSetPrice = snapToAllowedSetPrice(value);
              const actualPrice = mapSetPriceToActualPrice(snappedSetPrice);
              handlePriceChange("monthlyPrice", actualPrice);
            }}
          />
        </div>
      </div>

      <div className="mt-[5rem]">
        <p className="text-white uppercase md:text-lg">
          Set Direct Message Price
        </p>
        <div className="mt-12">
          <InputRange
            step={1}
            allowSameValues
            minValue={Math.min(...ALLOWED_MESSAGE_SET_VALUES)}
            maxValue={Math.max(...ALLOWED_MESSAGE_SET_VALUES)}
            value={currentMessagePriceDisplay}
            onChange={(basePrice) => {
              if (typeof basePrice !== "number") return;
              const snappedSetPrice = snapToAllowedMessageSetPrice(basePrice);
              const actualPrice =
                mapMessageSetPriceToActualPrice(snappedSetPrice);
              handlePriceChange("messagePrice", actualPrice);
            }}
          />
        </div>
      </div>

      <div className="mt-[5rem] md:mt-[8rem]">
        <CommonButton variant="light" onClick={applyChanges}>
          Apply
        </CommonButton>
      </div>

      <div className="mt-10 md:mt-14 text-white text-center">
        <p className="text-[15px] font-medium">Find your ideal pricing</p>
        <p className="text-xs mt-3">
          It's better to start with lower pricing to get maximum conversions.
        </p>
      </div>
    </P.Pricing>
  );
};

export default MembershipPricing;
